import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ExaminationCardApi } from '@core/apis/examination-card/examination-card.api';
import { ExaminationCardDTO } from '@core/apis/examination-card/examination-card.dto';
import { IExaminationCardGets } from '@core/apis/examination-card/examination-card.interface';
import { PatientDTO } from '@core/apis/patient/patient.dto';
import { PatientAboutShortComponent } from '@core/components/patient/patient-about-short/patient-about-short.component';
import { TranslocoHelper } from '@core/helpers/transloco.helper';
import { IPaginationGet } from '@core/interfaces/interface';
import { ExaminationCardService } from '@core/services/examination-card.service';
import { ClinicStore } from '@core/stores/clinic.store';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColumnPersonEnum, ITableChangesConfig, ITableRow, Table, TableColumn } from '@oranta-mis/ui-library';
import { IdentifyPatientListComponent } from '../identify-patient-list/identify-patient-list.component';
import { patientCardListColumnHeader } from './identify-measurement-dialog-list';
declare let bootstrap: any;

interface IRenderTable extends ITableRow<ExaminationCardDTO> {
  item: ExaminationCardDTO;
  type: string;
  createdAt: string | null;
}

@Component({
  selector: 'app-identify-measurement-dialog',
  imports: [
    TranslocoModule,
    CommonModule,
    FormsModule,
    Table,
    IdentifyPatientListComponent,
    PatientAboutShortComponent,
  ],
  templateUrl: './identify-measurement-dialog.component.html',
  styleUrls: ['./identify-measurement-dialog.component.scss'],
})
@UntilDestroy()
export class IdentifyMeasurementDialogComponent {
  private modalInstance!: any;

  @Input() public measurementId!: string;
  @Output() public identified = new EventEmitter<void>();

  public columnHeaderPatientCard!: TableColumn[];
  public patientCardsPaginated!: IPaginationGet<ExaminationCardDTO[]>;
  public patientCards: IRenderTable[] = [];
  public isLoading: boolean = false;

  public selectedPatient: PatientDTO | null = null;

  private clinicId!: number;
  private config!: ITableChangesConfig;

  constructor(
    private readonly examinationCardApi: ExaminationCardApi,
    private readonly examinationCardService: ExaminationCardService,
    private readonly clinicStore: ClinicStore,
    private readonly i18n: TranslocoHelper,
  ) {}

  ngOnInit(): void {
    this.columnHeaderPatientCard = patientCardListColumnHeader(this.i18n);
  }

  public setConfig(config: ITableChangesConfig): void {
    this.config = config;
    this.getCards(this.config);
  }

  public openModal() {
    const modalElement = document.getElementById('identifyMeasurementModal');
    if (modalElement) {
      this.modalInstance = new bootstrap.Modal(modalElement, {
        backdrop: 'static',
        keyboard: false,
      });
      this.modalInstance.show();
    }
  }

  public closeModal() {
    if (this.modalInstance) {
      this.modalInstance.hide();
    }
  }

  private renderObject(arr: ExaminationCardDTO[]): IRenderTable[] {
    const datePipe = new DatePipe('en-US');
    return arr.map((v) => {
      return {
        id: v._id,
        item: v,
        type: `<b>${this.i18n.tr(v.type)}</b>`,
        person: {
          type: ColumnPersonEnum.staff,
          data: v.staff,
        },
        diagnosis: v.data.diagnosis || '-',
        createdAt: datePipe.transform(v.createdAt, 'dd/MM/YYYY, HH:mm'),
      };
    });
  }

  private getCards(config?: ITableChangesConfig) {
    const clinic = this.clinicStore.get();
    if (clinic) this.clinicId = clinic.id;

    if (!this.selectedPatient) return;
    this.isLoading = true;
    let settings: IExaminationCardGets = {
      pagination: {
        page: config?.page || 1,
        perPage: config?.perPage || 10,
      },
      where: { clinicId: this.clinicId, patientId: this.selectedPatient.id },
    };

    if (config?.orderBy) settings = Object.assign({ orderBy: config.orderBy }, settings);

    this.examinationCardApi
      .gets(settings)
      .pipe(untilDestroyed(this))
      .subscribe((cards) => {
        this.patientCards = this.renderObject(cards.list);
        this.patientCardsPaginated = cards;
        this.isLoading = false;
      });
  }

  public selectPatient(patient: PatientDTO): void {
    if (!this.measurementId) return;
    this.selectedPatient = patient;
  }

  public selectCard(item: IRenderTable): void {
    if (!item.item || !this.measurementId) return;
    this.examinationCardService.identifyMeasurementForCard(item.item, this.measurementId);

    setTimeout(() => {
      this.identified.emit();
      this.selectedPatient = null;
      this.closeModal();
    }, 1500);
  }

  public goBackToPatientSelect(): void {
    this.selectedPatient = null;
  }
}
